import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText,
  DollarSign,
  Briefcase,
  TrendingUp,
  Lightbulb,
  BookOpen,
  CheckCircle,
  ArrowRight,
  X,
} from 'lucide-react';

const Services = ({ translations = {} }) => {
  const isSpanish = translations?.header?.home === 'Inicio';
  const [selectedService, setSelectedService] = useState(null);

  const copy = isSpanish
    ? {
        heroEyebrow: 'Soluciones empresariales',
        heroTitle:
          'Servicios para ordenar tu negocio antes de declarar, estructurar o buscar capital.',
        heroSubtitle:
          'Selecciona el área donde necesitas claridad. Cada solución está diseñada para revisar tu situación, organizar documentos y ayudarte a tomar el siguiente paso con menos incertidumbre.',
        primaryCta: 'Solicitar evaluación',
        secondaryCta: 'Ver soluciones',
        includesLabel: 'Incluye',
        modalTitle: 'Resumen de la solución',
        modalNextStep: 'Siguiente paso recomendado',
        modalButton: 'Solicitar evaluación',
        finalTitle: '¿No sabes por dónde empezar?',
        finalText:
          'Solicita una evaluación inicial y revisamos qué ruta tiene más sentido para tu situación actual.',
        finalButton: 'Solicitar evaluación',
        services: [
          {
            id: 'corporate-taxes',
            icon: FileText,
            title: 'Impuestos corporativos y LLC',
            hook: 'No esperes a que una carta del IRS te obligue a ordenar tu empresa.',
            description:
              'Preparamos y organizamos declaraciones para C-Corp, S-Corp y LLC con socios, cuidando que las formas, extensiones y reportes estén alineados con la estructura de tu negocio.',
            includes: [
              '1120, 1120-S y 1065',
              'Enmiendas 1120-X',
              'Extensiones y pagos estimados',
              'Reportes 941, 940, W-2, W-4 y 1099 cuando aplique',
            ],
            button: 'Ver solución fiscal',
            nextStep:
              'Revisar entidad, año fiscal, documentos disponibles y obligaciones pendientes antes de preparar la declaración.',
            route: '/services/corporate-taxes',
          },
          {
            id: 'personal-taxes',
            icon: DollarSign,
            title: 'Impuestos personales y small business',
            hook: 'Declara con claridad, no con improvisación.',
            description:
              'Para trabajadores independientes, familias y pequeños negocios que necesitan preparar su declaración, corregir años anteriores o responder documentos del IRS con más orden.',
            includes: [
              '1040 y 1040-X',
              'Schedule C, D, A y E',
              'W-7 / ITIN',
              'Cartas del IRS, planes de pago y enmiendas estatales',
            ],
            button: 'Ver solución personal',
            nextStep:
              'Identificar ingresos, dependientes, anexos necesarios, documentos faltantes y posibles asuntos pendientes con IRS o estado.',
            route: '/services/personal-taxes',
          },
          {
            id: 'business-structure',
            icon: Briefcase,
            title: 'Estructura empresarial',
            hook: 'Un negocio sin estructura termina dependiendo de la memoria del dueño.',
            description:
              'Formalizamos o reorganizamos la base empresarial para que tu negocio tenga entidad, documentación interna, EIN y presencia profesional.',
            includes: [
              'LLC / Corporation',
              'EIN',
              'Operating Agreement',
              'Cambios de clasificación 2553 / 8832',
            ],
            button: 'Ver estructura',
            nextStep:
              'Revisar si el negocio necesita crearse, corregirse o reorganizarse antes de mover impuestos, cuentas o documentación.',
            route: '/services/business-structure',
          },
          {
            id: 'business-profile',
            icon: TrendingUp,
            title: 'Perfil empresarial y preparación financiera',
            hook: 'Antes de buscar capital, tu empresa debe verse evaluable.',
            description:
              'Organizamos señales básicas de credibilidad empresarial: presencia profesional, DUNS, correo corporativo, teléfono comercial, website y cuentas proveedoras iniciales.',
            includes: [
              'DUNS',
              'Correo corporativo',
              'Website básico',
              'Teléfono comercial',
              'Cuentas proveedoras iniciales',
              'Plan de negocios base',
            ],
            button: 'Ver preparación',
            nextStep:
              'Evaluar qué señales empresariales faltan antes de presentar el negocio ante bancos, proveedores o sistemas comerciales.',
            route: '/services/business-profile',
          },
          {
            id: 'capital-map',
            icon: Lightbulb,
            title: 'Mapa de capital',
            hook: 'No toda ruta de capital conviene. Primero hay que saber dónde estás parado.',
            description:
              'Analizamos estructura, documentos, flujo, dependencia del dueño y posibles rutas financieras para definir 2 a 3 caminos realistas de preparación.',
            includes: [
              'Diagnóstico empresarial',
              'Revisión documental',
              '2 a 3 rutas posibles',
              'Siguiente paso recomendado',
            ],
            button: 'Ver mapa',
            nextStep:
              'Revisar estructura, documentos, ingresos, operación y nivel de preparación antes de considerar rutas externas.',
            route: '/services/capital-map',
          },
          {
            id: 'financial-education',
            icon: BookOpen,
            title: 'Educación financiera y revisión de perfil',
            hook: 'La claridad financiera evita decisiones caras.',
            description:
              'Acompañamiento educativo para entender reportes, hábitos, documentación y preparación antes de solicitar productos financieros externos.',
            includes: [
              'Revisión de perfil',
              'Organización financiera',
              'Educación sobre reportes',
              'Preparación para evaluación externa',
            ],
            button: 'Ver diagnóstico',
            nextStep:
              'Entender la situación actual del perfil financiero y definir qué debe ordenarse antes de tomar decisiones.',
            route: '/services/credit-repair',
          },
        ],
      }
    : {
        heroEyebrow: 'Business solutions',
        heroTitle:
          'Services to organize your business before filing, structuring, or preparing for capital.',
        heroSubtitle:
          'Choose the area where you need clarity. Each solution is designed to review your situation, organize documents, and help you take the next step with less uncertainty.',
        primaryCta: 'Request evaluation',
        secondaryCta: 'View solutions',
        includesLabel: 'Includes',
        modalTitle: 'Solution summary',
        modalNextStep: 'Recommended next step',
        modalButton: 'Request evaluation',
        finalTitle: 'Not sure where to start?',
        finalText:
          'Request an initial evaluation and we will review which route makes the most sense for your current situation.',
        finalButton: 'Request evaluation',
        services: [
          {
            id: 'corporate-taxes',
            icon: FileText,
            title: 'Corporate & LLC Taxes',
            hook: 'Do not wait for an IRS letter to force your company into order.',
            description:
              'We prepare and organize returns for C-Corps, S-Corps, and multi-member LLCs, keeping forms, extensions, and reports aligned with your business structure.',
            includes: [
              '1120, 1120-S and 1065',
              '1120-X amendments',
              'Extensions and estimated payments',
              '941, 940, W-2, W-4 and 1099 reports when applicable',
            ],
            button: 'View tax solution',
            nextStep:
              'Review entity type, tax year, available documents, and pending obligations before preparing the return.',
            route: '/services/corporate-taxes',
          },
          {
            id: 'personal-taxes',
            icon: DollarSign,
            title: 'Personal & Small Business Taxes',
            hook: 'File with clarity, not improvisation.',
            description:
              'For independent workers, families, and small businesses that need to file, amend prior years, or respond to IRS documents with more order.',
            includes: [
              '1040 and 1040-X',
              'Schedules C, D, A and E',
              'W-7 / ITIN',
              'IRS letters, payment plans and state amendments',
            ],
            button: 'View personal solution',
            nextStep:
              'Identify income, dependents, required schedules, missing documents, and possible IRS or state issues.',
            route: '/services/personal-taxes',
          },
          {
            id: 'business-structure',
            icon: Briefcase,
            title: 'Business Structure',
            hook: 'A business without structure ends up depending on the owner’s memory.',
            description:
              'We formalize or reorganize the business foundation so your company has an entity, internal documents, EIN, and professional presence.',
            includes: [
              'LLC / Corporation',
              'EIN',
              'Operating Agreement',
              'Entity classification changes 2553 / 8832',
            ],
            button: 'View structure',
            nextStep:
              'Review whether the business needs to be created, corrected, or reorganized before moving taxes, accounts, or documents.',
            route: '/services/business-structure',
          },
          {
            id: 'business-profile',
            icon: TrendingUp,
            title: 'Business Profile & Financial Readiness',
            hook: 'Before preparing for capital, your company must look reviewable.',
            description:
              'We organize basic credibility signals: professional presence, DUNS, corporate email, business phone, website, and initial vendor accounts.',
            includes: [
              'DUNS',
              'Corporate email',
              'Basic website',
              'Business phone',
              'Initial vendor accounts',
              'Base business plan',
            ],
            button: 'View readiness',
            nextStep:
              'Evaluate which business credibility signals are missing before presenting the company to banks, vendors, or business systems.',
            route: '/services/business-profile',
          },
          {
            id: 'capital-map',
            icon: Lightbulb,
            title: 'Capital Map',
            hook: 'Not every capital route makes sense. First, you need to know where you stand.',
            description:
              'We analyze structure, documents, cash flow, owner dependency, and possible financial routes to define 2 to 3 realistic preparation paths.',
            includes: [
              'Business diagnosis',
              'Document review',
              '2 to 3 possible routes',
              'Recommended next step',
            ],
            button: 'View map',
            nextStep:
              'Review structure, documents, income, operations, and readiness level before considering external routes.',
            route: '/services/capital-map',
          },
          {
            id: 'financial-education',
            icon: BookOpen,
            title: 'Financial Education & Profile Review',
            hook: 'Financial clarity prevents expensive decisions.',
            description:
              'Educational guidance to understand reports, habits, documentation, and preparation before applying for external financial products.',
            includes: [
              'Profile review',
              'Financial organization',
              'Report education',
              'Preparation for external evaluation',
            ],
            button: 'View diagnosis',
            nextStep:
              'Understand the current financial profile and define what needs to be organized before making decisions.',
            route: '/services/credit-repair',
          },
        ],
      };

  const openSolutionPopup = (service) => setSelectedService(service);
  const closeSolutionPopup = () => setSelectedService(null);

  return (
    <>
      <section className="relative min-h-[82vh] flex items-center bg-fut-bg-dark text-fut-text-light overflow-hidden border-b border-fut-border pt-32 md:pt-44 pb-16">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/businessman-3.webp"
            alt="San Jose Obrero LLC services"
            loading="eager"
            className="w-full h-full max-w-none object-cover object-center lg:object-[center_42%] opacity-75"          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-fut-bg-dark" aria-hidden="true" />
        </div>

        <div className="container mx-auto max-w-screen-xl px-6 relative z-20">
          <div className="max-w-4xl mx-auto text-center">
            <motion.p
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              className="text-fut-primary font-bold uppercase tracking-[0.22em] mb-4"
            >
              {copy.heroEyebrow}
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: -18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65 }}
              className="text-4xl md:text-6xl font-display leading-tight mb-6 text-white drop-shadow-lg"
            >
              {copy.heroTitle}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: -14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.12 }}
              className="text-lg md:text-2xl max-w-3xl mx-auto text-fut-text-light/90 font-semibold"
            >
              {copy.heroSubtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.22 }}
              className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
            >
              <a
                href="/contact"
                className="inline-flex items-center justify-center bg-fut-primary text-fut-bg-dark px-7 py-4 rounded-lg text-lg font-bold hover:bg-fut-secondary transition-all duration-300 shadow-fut-glow-md shadow-fut-primary"
              >
                {copy.primaryCta}
              </a>
              <a
                href="#solutions"
                className="inline-flex items-center justify-center border border-fut-primary text-fut-primary px-7 py-4 rounded-lg text-lg font-bold hover:bg-fut-primary hover:text-fut-bg-dark transition-all duration-300"
              >
                {copy.secondaryCta}
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="solutions" className="py-16 md:py-24 bg-fut-bg-dark border-b border-fut-border">
        <div className="container mx-auto max-w-screen-xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {copy.services.map((service, index) => (
              <motion.article
                key={service.id}
                initial={{ opacity: 0, y: 38 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: index * 0.04 }}
                className="group bg-fut-bg-light p-7 rounded-2xl shadow-fut-glow-sm shadow-fut-primary hover:shadow-fut-glow-md hover:shadow-fut-secondary transition-all duration-300 flex flex-col min-h-[520px]"
              >
                <div className="flex items-center justify-between gap-4 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-fut-primary/10 flex items-center justify-center">
                    <service.icon size={36} className="text-fut-primary drop-shadow-lg" />
                  </div>
                  <span className="text-xs uppercase tracking-[0.18em] text-fut-primary/80 font-bold">
                    SJO
                  </span>
                </div>

                <h3 className="text-2xl md:text-3xl font-display text-fut-text-light mb-4 leading-tight">
                  {service.title}
                </h3>

                <p className="text-fut-primary font-bold mb-4 leading-relaxed">
                  {service.hook}
                </p>

                <p className="text-fut-text-light/80 mb-6 leading-relaxed">
                  {service.description}
                </p>

                <div className="mt-auto">
                  <h4 className="text-sm uppercase tracking-[0.18em] font-bold text-fut-primary mb-3">
                    {copy.includesLabel}
                  </h4>

                  <ul className="space-y-2 mb-7">
                    {service.includes.slice(0, 4).map((item) => (
                      <li key={item} className="flex gap-3 text-fut-text-light/85">
                        <CheckCircle size={18} className="text-fut-primary mt-1 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => {
                    if (service.id === 'financial-education') {
                      window.location.href = service.route;
                      return;
                    }
                    openSolutionPopup(service);
                  }}
                    className="w-full inline-flex items-center justify-center gap-2 bg-fut-secondary text-fut-text-light px-6 py-3 rounded-lg text-lg font-bold hover:bg-fut-primary hover:text-fut-bg-dark transition-all duration-300 shadow-fut-glow-md shadow-fut-secondary"
                  >
                    {service.button}
                    <ArrowRight size={18} />
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-fut-bg-dark text-fut-text-light relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/cta/cta-2.webp"
            alt="San Jose Obrero LLC CTA"
            loading="lazy"
            className="w-full h-full max-w-none object-cover object-center opacity-70"          />
          <div className="absolute inset-0 bg-black/70" aria-hidden="true" />
        </div>

        <div className="container mx-auto max-w-screen-xl px-6 relative z-20 text-center">
          <motion.h2
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="text-3xl md:text-5xl font-display text-fut-primary drop-shadow-lg mb-5"
          >
            {copy.finalTitle}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: -8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="text-lg md:text-xl text-fut-text-light/90 max-w-3xl mx-auto"
          >
            {copy.finalText}
          </motion.p>

          <motion.a
            href="/contact"
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.2 }}
            className="inline-block mt-8 bg-fut-primary text-fut-bg-dark px-7 py-4 rounded-lg text-lg font-bold hover:bg-fut-secondary transition-all duration-300 shadow-fut-glow-md shadow-fut-primary"
          >
            {copy.finalButton}
          </motion.a>
        </div>
      </section>

      <AnimatePresence>
        {selectedService && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50"
            onClick={closeSolutionPopup}
          >
            <motion.div
              initial={{ scale: 0.94, y: 40 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 40 }}
              transition={{ type: 'spring', stiffness: 120, damping: 18 }}
              className="bg-fut-bg-light rounded-2xl shadow-fut-glow-lg shadow-fut-primary p-7 md:p-9 max-w-3xl w-full max-h-[90vh] overflow-y-auto relative text-fut-text-light"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeSolutionPopup}
                className="absolute top-4 right-4 text-fut-text-light/70 hover:text-fut-primary transition-colors duration-200"
                aria-label="Close"
              >
                <X size={28} />
              </button>

              <div className="w-16 h-16 rounded-2xl bg-fut-primary/10 flex items-center justify-center mb-6">
                <selectedService.icon size={36} className="text-fut-primary" />
              </div>

              <h2 className="text-3xl font-display text-fut-primary mb-3 pr-8">
                {selectedService.title}
              </h2>

              <p className="text-xl font-bold text-fut-text-light mb-5">
                {selectedService.hook}
              </p>

              <p className="text-fut-text-light/85 leading-relaxed mb-6">
                {selectedService.description}
              </p>

              <h3 className="text-fut-primary font-bold uppercase tracking-[0.18em] mb-3">
                {copy.includesLabel}
              </h3>

              <ul className="space-y-3 mb-7">
                {selectedService.includes.map((item) => (
                  <li key={item} className="flex gap-3 text-fut-text-light/85">
                    <CheckCircle size={19} className="text-fut-primary mt-1 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="rounded-2xl bg-fut-bg-dark/40 p-5 mb-7">
                <p className="text-fut-primary font-bold mb-2">
                  {copy.modalNextStep}
                </p>
                <p className="text-fut-text-light/85">
                  {selectedService.nextStep}
                </p>
              </div>

              <a
                href="/contact"
                className="inline-flex w-full items-center justify-center gap-2 bg-fut-primary text-fut-bg-dark px-6 py-4 rounded-lg text-lg font-bold hover:bg-fut-secondary transition-all duration-300"
              >
                {copy.modalButton}
                <ArrowRight size={18} />
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Services;
